import React from 'react';
import { useSelector } from 'react-redux';
import Helmet from 'react-helmet';
import { Button, LinearProgress } from '@material-ui/core';
import { Tooltip } from '@mui/material';
import { useHistory } from 'react-router';

import InfoCirclePurple from 'assets/icons/info-circle-purple.svg';
import { selectUser } from 'entities/user/selectors';
import Box from 'retired/Box';
import OrganizationMenu from 'retired/OrganizationMenu';
import { computeDonationProviderForNonprofit } from 'utils/computeDonationProviderForNonprofit';
import { useContent } from 'components/Flow/flows/nonprofitOnboardingPayment/components/useContent';
import { ModalTitle, ModalTypography } from 'components/Modal/Modal';
import Img from 'retired/Img';
import { OrganizationType } from 'generated/graphql';
import { NonprofitEntityType } from 'entities/organization/model';
import { UserState } from 'entities/user/reducer';

import { Privileges } from '../../utils/privileges';

export const paymentsRouteIsPotentiallyEnabled = (user: UserState) =>
  user?.organization?.type === OrganizationType.Nonprofit &&
  user?.organization?.nonprofitEntityType !== NonprofitEntityType.governmentEntity &&
  user?.organization?.nonprofitEntityType !== NonprofitEntityType.memberAssociatedEntity;

export const paymentsRouteIsEnabled = (user: UserState) =>
  paymentsRouteIsPotentiallyEnabled(user) &&
  user?.hasPrivilege(Privileges.ORGANIZATION_MANAGE_SETTINGS);

const PaymentsRoute = () => {
  const user = useSelector(selectUser);
  const history = useHistory();

  if (user && !paymentsRouteIsEnabled(user)) {
    history.replace('/');
  }

  const donationProvider =
    user?.organization &&
    computeDonationProviderForNonprofit(
      user?.organization,
      user?.organization?.settings?.get('preferStripe') || false
    );

  const content = useContent(
    donationProvider,
    user?.organization,
    user?.hasPrivilege(Privileges.ORGANIZATION_SELF_VERIFIED)
  );

  return (
    <>
      <Helmet title={content.title} />
      {(user?.hasPrivilege(Privileges.ORGANIZATION_MANAGE_SETTINGS) ||
        user?.hasPrivilege(Privileges.ORGANIZATION_VIEW_ORGANIZERS)) && (
        <div style={{ marginBottom: 60 }}>
          <OrganizationMenu user={user} />
        </div>
      )}
      {!user && (
        <div style={{ maxWidth: 1000 }}>
          <LinearProgress />
        </div>
      )}
      <Box style={{ maxWidth: 1000 }}>
        <h2>{content.title}</h2>

        <div style={{ maxWidth: 700 }}>
          <ModalTitle style={{ lineHeight: '28px' }}>
            {content.header}
            {content.tooltip && (
              <Tooltip placement="right" title={content.tooltip}>
                <span
                  style={{ display: 'inline-block', marginLeft: 4, transform: 'translateY(-2px)' }}
                >
                  <Img
                    src={InfoCirclePurple}
                    style={{
                      height: 'max-content',
                    }}
                  />
                </span>
              </Tooltip>
            )}
          </ModalTitle>

          <ModalTypography>{content.body}</ModalTypography>

          {user?.isAdmin && (
            <>
              {user?.organization?.externalId && (
                <div>
                  External Id: <code>{user.organization.externalId}</code>
                </div>
              )}
            </>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'row-reverse' }}>
          <Button
            type="button"
            variant="contained"
            onClick={() => {
              window.open(content.link, '_blank')?.focus();
            }}
          >
            {content.ctaText}
          </Button>
        </div>
      </Box>
    </>
  );
};

export default PaymentsRoute;
